import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { needsApi, usersApi } from '../services/api';
import { 
  User, Building, AlertCircle, Edit, Trash2,
  CheckCircle, XCircle, Archive, Loader, Eye
} from 'lucide-react';
import Badge from '../components/ui/Badge';
import Toast from '../components/ui/Toast';
import EditNeedModal from '../components/recruiting/EditNeedModal';
import ProcessTracker from '../components/recruiting/ProcessTracker';
import HardDeleteConfirmDialog from '../components/recruiting/HardDeleteConfirmDialog';
import AppHeader from '../components/common/AppHeader';
import Pagination from '../components/ui/Pagination';
import { useAuth } from '../contexts/AuthContext';
import { useFinishedNeeds, useSofteamboardStore } from '../store/softeamboardStore';
import useDebounce from '../hooks/useDebounce';

// Move finishedStatuses OUTSIDE the component to keep stable reference
const FINISHED_STATUSES = [
    { id: 'SALE', label: 'Sold', color: 'success', icon: CheckCircle },
    { id: 'CANCELED', label: 'Canceled', color: 'warning', icon: XCircle },
    { id: 'DELETED', label: 'Deleted', color: 'danger', icon: Trash2 }
];

const FinishedNeeds = () => {
  // ✅ ADD: Get current user to check admin permissions
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  // Live data from the Provider
  const needs = useFinishedNeeds();
  const { refreshNeeds, initialized, removeNeedFromCache, error } = useSofteamboardStore();
  
  // ✅ FIX: Use initialized flag directly
  const loading = !initialized && !error;

  // Local state instead of depending on store
  const [consultants, setConsultants] = useState([]);

  // Filter and UI state
  const [searchValue, setSearchValue] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Modal state
  const [editModal, setEditModal] = useState({
    isOpen: false,
    need: null
  });
  const [toast, setToast] = useState(null);

  // ✅ ADD: Hard delete confirmation dialog state
  const [hardDeleteDialog, setHardDeleteDialog] = useState({ isOpen: false, need: null });

  // ✅ ADD: ProcessTracker modal state
  const [processTrackerModal, setProcessTrackerModal] = useState({
    isOpen: false,
    needId: null
  });

  // Debounced search
  const debouncedSearch = useDebounce(searchValue, 300);

  // ✅ ADD: Pagination state (client-side)
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0
  });

  // Load consultants/sales users
  const loadConsultants = useCallback(async () => {
    try {
      const response = await usersApi.listUsers({ role: 'Sales', limit: 100, offset: 0 });
      setConsultants(response.items || []);
    } catch (err) {
      console.error('Failed to load consultants:', err.message);
      setConsultants([]);
    }
  }, []);

  // Apply client-side filtering and sorting
  const filteredAndSortedNeeds = useMemo(() => {
    let filtered = needs;

    // Apply search filter
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      filtered = filtered.filter(need => 
        need.title.toLowerCase().includes(searchLower) ||
        need.client.toLowerCase().includes(searchLower) ||
        (need.description && need.description.toLowerCase().includes(searchLower))
      );
    }

    // Apply status filter
    if (selectedStatus) {
      filtered = filtered.filter(need => 
        need.column === selectedStatus || need.status === selectedStatus
      );
    }

    // Apply sorting
    if (sortBy) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortBy) {
          case 'title':
            aValue = a.title.toLowerCase();
            bValue = b.title.toLowerCase();
            break;
          case 'client':
            aValue = a.client.toLowerCase();
            bValue = b.client.toLowerCase();
            break;
          case 'updatedAt':
            aValue = new Date(a.updatedAt || 0);
            bValue = new Date(b.updatedAt || 0);
            break;
          default:
            aValue = a[sortBy];
            bValue = b[sortBy];
        }

        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }

    return filtered;
  }, [needs, debouncedSearch, selectedStatus, sortBy, sortOrder]);

  // ✅ ADD: Paginated results (client-side pagination)
  const paginatedNeeds = useMemo(() => {
    const start = pagination.offset;
    const end = start + pagination.limit;
    return filteredAndSortedNeeds.slice(start, end);
  }, [filteredAndSortedNeeds, pagination.offset, pagination.limit]);

  // ✅ ADD: Total count for pagination
  const totalNeeds = filteredAndSortedNeeds.length;

  // ✅ ADD: Pagination handlers
  const handleNextPage = useCallback(() => {
    setPagination(prev => ({
      ...prev,
      offset: prev.offset + prev.limit
    }));
  }, []);

  const handlePreviousPage = useCallback(() => {
    setPagination(prev => ({
      ...prev,
      offset: Math.max(0, prev.offset - prev.limit)
    }));
  }, []);

  // ✅ ADD: Reset to first page when filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, offset: 0 }));
  }, [debouncedSearch, selectedStatus, sortBy, sortOrder]);

  // ✅ FIXED: Always ensure fresh data on page load
  useEffect(() => {
    loadConsultants();
  }, [loadConsultants]);

  // ✅ FIXED: Use ref to prevent infinite loop - only refresh on mount
  useEffect(() => {
    // Always refresh needs data when component mounts
    // This ensures we have current data even if cache exists
    refreshNeeds();
  }, []); // Remove refreshNeeds dependency to prevent infinite loop

  // ✅ ALTERNATIVE: If you want to be more conservative, use this instead:
  // useEffect(() => {
  //   // Force refresh if no data, or if cache might be stale
  //   if (!needs.length || !initialized) {
  //     refreshNeeds();
  //   }
  // }, [needs.length, initialized]); // Only depend on data state, not functions

  // Handle status filter
  const handleStatusFilter = useCallback((status) => {
    setSelectedStatus(status === selectedStatus ? '' : status);
  }, [selectedStatus]);

  // Handle sort
  const handleSort = useCallback((field) => {
    let newSortOrder = 'desc';
    if (sortBy === field && sortOrder === 'desc') {
      newSortOrder = 'asc';
    }
    
    setSortBy(field);
    setSortOrder(newSortOrder);
  }, [sortBy, sortOrder]);

  // Handle edit click
  const handleEditClick = useCallback((need) => {
    setEditModal({ isOpen: true, need });
  }, []);

  const handleEditModalClose = useCallback(() => {
    setEditModal({ isOpen: false, need: null });
  }, []);

  const handleEditSave = useCallback(() => {
    refreshNeeds();
    setToast({ message: 'Need updated successfully', type: 'success' });
    handleEditModalClose();
  }, [handleEditModalClose, refreshNeeds]);

  // Helper functions - use stable FINISHED_STATUSES constant
  const getStatusInfo = useCallback((status) => {
    return FINISHED_STATUSES.find(s => s.id === status) || 
        { id: status, label: status, color: 'gray', icon: Archive };
  }, []);

  const getSalesNames = useCallback((consultantIds) => {
    if (!consultantIds || consultantIds.length === 0) return '–';
    return consultantIds
      .map(id => consultants.find(c => c.id === id)?.name || 'Unknown')
      .join(', ');
  }, [consultants]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '–';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return '–';
    }
  }, []);

  const clearAllFilters = useCallback(() => {
    setSearchValue('');
    setSelectedStatus('');
    setSortBy('updatedAt');
    setSortOrder('desc');
  }, []);

  // ✅ ADD: Hard delete handler
  const handleHardDelete = async (need) => {
    try {
      await needsApi.hardDeleteNeed(need.id);
      
      // ✅ ADD: Remove from store cache immediately
      removeNeedFromCache(need.id);
      
      // Show success toast
      setToast({
        show: true,
        message: `Need "${need.title}" has been permanently deleted`,
        type: 'success'
      });
      
      console.log('✅ Need permanently deleted:', need.id);
    } catch (error) {
      console.error('❌ Error hard deleting need:', error);
      
      let errorMessage = 'Failed to delete need';
      if (error.response?.status === 403) {
        errorMessage = 'Admin privileges required for permanent deletion';
      } else if (error.response?.status === 404) {
        errorMessage = 'Need not found';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setToast({
        show: true,
        message: errorMessage,
        type: 'error'
      });
      throw error;
    }
  };

  // ✅ ADD: Handler for opening ProcessTracker
  const handleViewNeed = useCallback((need) => {
    setProcessTrackerModal({
      isOpen: true,
      needId: need.id
    });
  }, []);

  // ✅ ADD: Handler for closing ProcessTracker
  const handleCloseProcessTracker = useCallback(() => {
    setProcessTrackerModal({
      isOpen: false,
      needId: null
    });
  }, []);

  // ✅ FIXED: Handler for need update from ProcessTracker - use ref to prevent dependency loop
  const handleNeedUpdate = useCallback(() => {
    // Refresh the needs list when a need is updated
    refreshNeeds();
  }, []); // Remove refreshNeeds dependency to prevent infinite loop

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        showFilterButton={true}
      />

      <div className="p-6 space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Archive size={24} className="text-gray-600" />
            <h1 className="text-2xl font-bold text-gray-900">Finished Needs</h1>
            <span className="text-sm text-gray-500">
              Showing {pagination.offset + 1}-{Math.min(pagination.offset + paginatedNeeds.length, totalNeeds)} of {totalNeeds} total
            </span>
          </div>
          {/* ✅ ADD: Admin notice */}
          {isAdmin && (
            <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
              Admin: Hard delete available
            </div>
          )}
        </div>

        {/* Filters Panel */}
        {showFilters && (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-900">Filters</h3>
            <button
                onClick={clearAllFilters}
                className="text-sm text-blue-600 hover:text-blue-800"
            >
                Clear all
            </button>
            </div>

            {/* Status Filter - FIXED: use proper status values */}
            <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="flex flex-wrap gap-2">
                {FINISHED_STATUSES.map(status => {
                const Icon = status.icon;
                return (
                    <button
                    key={status.id}
                    onClick={() => handleStatusFilter(status.id)}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm border transition-colors ${
                        selectedStatus === status.id
                        ? 'bg-blue-100 border-blue-300 text-blue-800'
                        : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                    }`}
                    >
                    <Icon size={14} className="mr-1" />
                    {status.label}
                    </button>
                );
                })}
            </div>
            </div>
        </div>
        )}

        {/* Error Display */}
        {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading needs</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
            </div>
        </div>
        )}

        {/* Loading State */}
        {loading && (
        <div className="flex items-center justify-center h-64">
            <Loader className="animate-spin mr-2" size={24} />
            <span>Loading finished needs...</span>
        </div>
        )}

        {/* Needs Table */}
        {!loading && (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
                <tr>
                <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('title')}
                >
                    <div className="flex items-center">
                    Title
                    {sortBy === 'title' && (
                        <span className="ml-1">
                        {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                    )}
                    </div>
                </th>
                <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('client')}
                >
                    <div className="flex items-center">
                    Client
                    {sortBy === 'client' && (
                        <span className="ml-1">
                        {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                    )}
                    </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sales
                </th>
                <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('updatedAt')}
                >
                    <div className="flex items-center">
                    Finished
                    {sortBy === 'updatedAt' && (
                        <span className="ml-1">
                        {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                    )}
                    </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                </th>
                </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
                {paginatedNeeds.map((need) => {
                const statusInfo = getStatusInfo(need.column || need.status);
                const StatusIcon = statusInfo.icon;
                
                return (
                    <tr
                        key={need.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleViewNeed(need)}
                        title="Click to view need details"
                        tabIndex={0}
                        role="button"
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleViewNeed(need); }}}
                    >
                    <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{need.title}</div>
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                        {need.description}
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                        <Building className="mr-2 text-gray-400" size={16} />
                        <span className="text-sm text-gray-900">{need.client}</span>
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={need.priority === 'P1' ? 'danger' : need.priority === 'P2' ? 'warning' : 'info'}>
                        {need.priority}
                        </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                        <StatusIcon className="mr-2" size={16} />
                        <Badge variant={statusInfo.color}>
                            {statusInfo.label}
                        </Badge>
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                        <User className="mr-2 text-gray-400" size={16} />
                        <span className="text-sm text-gray-900">{getSalesNames(need.consultants)}</span>
                        </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(need.updatedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleViewNeed(need);
                            }}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                            title="View need details"
                            aria-label="View need details"
                        >
                            <Eye size={16} />
                        </button>
                        
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditClick(need);
                            }}
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                            title="Edit need"
                            aria-label="Edit need"
                        >
                            <Edit size={16} />
                        </button>
                        
                        {/* ✅ ADD: Hard Delete Button (Admin Only) */}
                        {isAdmin && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setHardDeleteDialog({ isOpen: true, need });
                                }}
                                className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                title="Permanently delete this need and all related data"
                                aria-label="Hard delete need"
                            >
                                <Trash2 size={16} />
                            </button>
                        )}
                        </div>
                    </td>
                    </tr>
                );
                })}
            </tbody>
            </table>
            </div>

            {/* ✅ ADD: Pagination controls */}
            {!loading && paginatedNeeds.length > 0 && (
              <Pagination
                limit={pagination.limit}
                offset={pagination.offset}
                total={totalNeeds}
                onNext={handleNextPage}
                onPrevious={handlePreviousPage}
              />
            )}

            {/* Empty state message - IMPROVED */}
            {totalNeeds === 0 && !loading && (
            <div className="text-center py-12">
                <Archive className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {selectedStatus ? `No ${selectedStatus.toLowerCase()} needs found` : 'No finished needs found'}
                </h3>
                <p className="text-gray-500">
                    {searchValue || selectedStatus 
                        ? "Try adjusting your search or filters"
                        : "Needs will appear here when they are marked as sold, canceled, or deleted"
                    }
                </p>
            </div>
            )}
        </div>
        )}
    </div>

    {/* ✅ ADD: ProcessTracker Modal */}
    {processTrackerModal.isOpen && (
        <ProcessTracker
            needId={processTrackerModal.needId}
            isOpen={processTrackerModal.isOpen}
            onClose={handleCloseProcessTracker}
            onNeedUpdate={handleNeedUpdate}
        />
    )}

    {/* Edit Modal */}
    {editModal.isOpen && (
        <EditNeedModal
        need={editModal.need}
        open={editModal.isOpen}
        onClose={handleEditModalClose}
        onSuccess={handleEditSave}
        />
    )}

    {/* ✅ ADD: Hard Delete Confirmation Dialog */}
    <HardDeleteConfirmDialog
        isOpen={hardDeleteDialog.isOpen}
        onClose={() => setHardDeleteDialog({ isOpen: false, need: null })}
        onConfirm={() => handleHardDelete(hardDeleteDialog.need)}
        needTitle={hardDeleteDialog.need?.title || ''}
    />

    {/* Toast */}
    {toast && (
        <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(null)}
        />
    )}
    </div>
);
};

export default FinishedNeeds;
